import styled from 'styled-components';
import { Theme } from '@salecycle/ui-component-library';
import { PropsWithChildren } from 'react';
import { capitalizeFirstLetter } from 'core/ui/utils/capitalizeFirstLetter';

export type TypographyFontVariant = 'link' | 'italic' | 'semibold';
export type TypographyVariant = 'headingH1' | 'headingH2' | 'headingH3' | 'body1' | 'body2';

const getDesignTokenForVariant = (theme: Theme, variant: TypographyVariant, fontVariant: TypographyFontVariant) => {
  const fv = capitalizeFirstLetter(fontVariant);

  switch (fontVariant) {
    case 'link':
    case 'italic':
    case 'semibold':
      return `${theme.designToken[variant + fv]}`;
    default:
      return `${theme.designToken[variant]}`;
  }
};

type TypographyWithVariants<TVariants> = {
  variant?: TVariants;
  fontVariant?: TypographyFontVariant;
};

const H1 = styled.h1`
  font: ${({ theme }) => theme.designToken.headingH1};
  margin-bottom: 0;
`;
const H2 = styled.h2`
  font: ${({ theme }) => theme.designToken.headingH2};
  margin-bottom: 0;
`;
const H3 = styled.h3<TypographyWithVariants<'headingH3'>>`
  font: ${({ variant, fontVariant, theme }) => getDesignTokenForVariant(theme, variant, fontVariant)};
  margin-bottom: 0;
`;
const Body = styled.p<TypographyWithVariants<'body1' | 'body2'>>`
  font: ${({ variant, fontVariant, theme }) => getDesignTokenForVariant(theme, variant, fontVariant)};
  margin-bottom: 0;
`;

export type TypographyProps = TypographyWithVariants<TypographyVariant> & IntrinsicProps;

export const Typography = ({ variant, ...props }: PropsWithChildren<TypographyProps>) => {
  switch (variant) {
    case 'headingH1':
      return <H1 {...props} />;
    case 'headingH2':
      return <H2 {...props} />;
    case 'headingH3':
      return <H3 variant="headingH3" {...props} />;
    default:
      return <Body variant={variant} {...props} />;
  }
};

Typography.defaultProps = {
  variant: 'body1',
};

export const T = Typography;
